"use client";
import Image from "next/image";
import Link from "next/link";
import { LuTrash2 } from "react-icons/lu";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "~/components/v3/shadcn/Card";
import { env } from "~/env.mjs";
import { api } from "~/utils/api";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "../v3/shadcn/Carousel";
import { Button } from "../v3/themed/Button";
import { cn } from "~/utils/fontsConfig";
import { UnifiedProduct } from "./types";

type ProductListProps = {
    communitySlug?: string;
    products?: UnifiedProduct[];
};

const ProductList = ({ communitySlug, products }: ProductListProps) => {
    if (!communitySlug && !products) {
        return null;
    }

    return (
        <div className="mb-10 flex max-h-[320px] flex-wrap">
            {products?.map((product) => (
                <Card
                    key={product.id}
                    className="mb-2 mt-2 h-full w-full cursor-pointer transition-shadow hover:shadow-lg md:max-w-[250px]"
                >
                    <Link href={product.url} key={product.id} target="_blank">
                        <CardHeader>
                            <p>{product.name}</p>
                        </CardHeader>
                        <CardContent>
                            {product.image && (
                                <Image
                                    src={
                                        product.image.startsWith("http")
                                            ? product.image
                                            : `${env.NEXT_PUBLIC_ASSETS_URL}/community-merchandise/${communitySlug}/${product.image}`
                                    }
                                    alt={product.altText || product.name}
                                    width={250}
                                    height={250}
                                    objectFit="contain"
                                />
                            )}
                            {product.description && (
                                <p>{product.description}</p>
                            )}
                        </CardContent>
                    </Link>
                </Card>
            ))}
        </div>
    );
};

type Props = {
    displayAffiliateProducts?: boolean | null;
    communitySlug?: string | null;
    onDelete?: (id: string) => void;
    className?: string;
    itemsClassName?: string;
};

export const ProductCarousel = ({
    displayAffiliateProducts,
    communitySlug,
    onDelete,
    className,
    itemsClassName,
}: Props) => {
    const { data: products } =
        api.merchandise.getAllProductsForCommunity.useQuery(
            {
                communitySlug: communitySlug as string,
            },
            {
                enabled: !!communitySlug,
            },
        );

    if (!displayAffiliateProducts || !products || products.length === 0) {
        return null;
    }

    return (
        <Card className={cn("max-w-[98vw] md:mx-0", className)}>
            <CardContent className="p-4">
                <CardHeader className="p-0 pb-4">
                    <CardTitle>Merch</CardTitle>
                </CardHeader>
                <Carousel className="w-full">
                    <CarouselContent className="-ml-1">
                        {products.map((product, index) => (
                            <CarouselItem
                                key={index}
                                className={cn(
                                    "pl-1 md:basis-1/3",
                                    itemsClassName,
                                )}
                            >
                                <div className="p-1">
                                    <Card className="relative p-2">
                                        {onDelete && (
                                            <Button
                                                variant="destructive"
                                                className="absolute right-1 top-1 rounded"
                                                size="sm"
                                                onClick={() =>
                                                    onDelete(product.id)
                                                }
                                            >
                                                <LuTrash2 />
                                            </Button>
                                        )}
                                        <CardContent className="flex flex-col items-center justify-center p-3">
                                            <Link
                                                href={product.url}
                                                target="_blank"
                                                className="flex max-w-[180px] flex-col items-center justify-center"
                                            >
                                                <h6 className="line-clamp-1 text-[18px]">
                                                    {product.name}
                                                </h6>
                                                {product.image && (
                                                    <Image
                                                        src={
                                                            product.image.startsWith(
                                                                "http",
                                                            )
                                                                ? product.image
                                                                : `${env.NEXT_PUBLIC_ASSETS_URL}/community-merchandise/${communitySlug}/${product.image}`
                                                        }
                                                        alt={
                                                            product.altText ||
                                                            product.name
                                                        }
                                                        width={150}
                                                        height={150}
                                                        objectFit="contain"
                                                    />
                                                )}
                                                {product.description && (
                                                    <p>{product.description}</p>
                                                )}
                                            </Link>
                                        </CardContent>
                                    </Card>
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    <CarouselPrevious />
                    <CarouselNext />
                </Carousel>
            </CardContent>
        </Card>
    );
};

export default ProductList;
